import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Hint,
  SelectorChip,
  Skeleton,
} from '@rammetec/design-system'
import { Building2, Check, UserRound } from 'lucide-react'
import {
  useSessionManager,
  useUserOrganizations,
  useUserProfiles,
} from '@syscolect/application'
import { authService } from '@/services/authService'

interface SessionChoice {
  id: string
  label: string
  shortLabel?: string
}

function shortOrLabel(choice: SessionChoice | undefined, fallback: string | null): string {
  return choice?.shortLabel || choice?.label || fallback || '...'
}

/**
 * Below `sm` the header can't fit org + profile labels next to the warning,
 * bell, assistant and user menu, so the chips collapse to their icon. The
 * current choice stays visible in the dropdown (check mark) and in the Hint.
 * SelectorChip renders `icon span · label span · chevron svg`; these variants
 * hide the last two.
 */
const ICON_ONLY_ON_MOBILE = 'max-sm:px-2 max-sm:[&>span+span]:hidden max-sm:[&>svg]:hidden'

function SelectorSkeleton() {
  return <Skeleton className="hidden h-8 w-20 rounded-full sm:block" />
}

/**
 * Header org/profile switchers. Sources orgs/profiles from Syscolect's session
 * hooks and switches via `useSessionManager`. Mapped to `{ id, label, shortLabel }`.
 */
export function SessionSelectors() {
  const { currentOrganizationId, currentProfileId, switchOrganization, switchProfile } =
    useSessionManager()
  const { userOrganizations, isLoading: isLoadingOrgs } = useUserOrganizations()
  const { profiles: userProfiles, isLoading: isLoadingProfiles } = useUserProfiles()

  if (!authService.getCurrentUser()) return null

  const organizations: SessionChoice[] = userOrganizations.map((org) => ({
    id: org.id,
    label: org.name,
    shortLabel: org.abbreviation,
  }))
  const profiles: SessionChoice[] = userProfiles.map((profile) => ({
    id: profile.id,
    label: profile.name,
    shortLabel: profile.abbreviation,
  }))

  const currentOrganization = organizations.find((org) => org.id === currentOrganizationId)
  const currentProfile = profiles.find((profile) => profile.id === currentProfileId)

  return (
    <div className="flex items-center gap-2">
      {isLoadingProfiles && currentOrganizationId ? (
        <SelectorSkeleton />
      ) : currentOrganizationId && profiles.length > 0 ? (
        <DropdownMenu>
          <Hint
            label={currentProfile?.label ? `Perfil: ${currentProfile.label}` : 'Selecionar perfil'}
            shortcut="Ctrl+Alt+W"
            side="bottom"
          >
            <DropdownMenuTrigger asChild>
              <SelectorChip
                icon={<UserRound />}
                label={shortOrLabel(currentProfile, currentProfileId)}
                aria-label={`Perfil: ${currentProfile?.label ?? shortOrLabel(currentProfile, currentProfileId)}`}
                className={ICON_ONLY_ON_MOBILE}
                disabled={profiles.length <= 1}
              />
            </DropdownMenuTrigger>
          </Hint>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel className="flex items-center gap-2 text-xs text-fg-muted">
              <UserRound className="h-3.5 w-3.5" />
              Perfis
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {profiles.map((profile) => (
              <DropdownMenuItem
                key={profile.id}
                onSelect={() => {
                  void switchProfile(profile.id)
                }}
                disabled={profile.id === currentProfileId}
                className="gap-2"
              >
                {profile.id === currentProfileId ? (
                  <Check className="h-4 w-4 text-accent" />
                ) : (
                  <UserRound className="h-4 w-4 text-fg-muted" />
                )}
                <span className="flex min-w-0 flex-col">
                  <span className="truncate">{profile.label}</span>
                  {profile.shortLabel && profile.shortLabel !== profile.label ? (
                    <span className="truncate text-xs text-fg-muted">{profile.shortLabel}</span>
                  ) : null}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}

      {isLoadingOrgs ? (
        <SelectorSkeleton />
      ) : organizations.length > 0 ? (
        <DropdownMenu>
          <Hint
            label={currentOrganization?.label ? `Organização: ${currentOrganization.label}` : 'Selecionar organização'}
            shortcut="Ctrl+Alt+E"
            side="bottom"
          >
            <DropdownMenuTrigger asChild>
              <SelectorChip
                icon={<Building2 />}
                label={shortOrLabel(currentOrganization, currentOrganizationId)}
                aria-label={`Organização: ${currentOrganization?.label ?? shortOrLabel(currentOrganization, currentOrganizationId)}`}
                className={ICON_ONLY_ON_MOBILE}
                disabled={organizations.length <= 1}
              />
            </DropdownMenuTrigger>
          </Hint>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel className="flex items-center gap-2 text-xs text-fg-muted">
              <Building2 className="h-3.5 w-3.5" />
              Organizações
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {organizations.map((organization) => (
              <DropdownMenuItem
                key={organization.id}
                onSelect={() => {
                  void switchOrganization(organization.id)
                }}
                disabled={organization.id === currentOrganizationId}
                className="gap-2"
              >
                {organization.id === currentOrganizationId ? (
                  <Check className="h-4 w-4 text-accent" />
                ) : (
                  <Building2 className="h-4 w-4 text-fg-muted" />
                )}
                <span className="flex min-w-0 flex-col">
                  <span className="truncate">{organization.label}</span>
                  {organization.shortLabel && organization.shortLabel !== organization.label ? (
                    <span className="truncate text-xs text-fg-muted">{organization.shortLabel}</span>
                  ) : null}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  )
}
